import React, { useEffect, useState } from 'react';
import { Form, Row, Space } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import numeral from 'numeral';
import {
  GoogleMap,
  Marker,
  withGoogleMap,
  withScriptjs,
} from 'react-google-maps';
import {
  RBHBox,
  RBHButton,
  RBHImage,
  RBHText,
  RBHTextInput,
} from '../../../../../../shared/components';
import { Images } from '../../../../../../shared/theme';
import styleButton from '../../../../../../shared/components/rbh-button/styles';
import styleModal from '../../../../../../shared/components/rbh-modal/styles';
import { UtilHelper } from '../../../../../../shared/helpers/utility';
import { envConfig } from '../../../../../../config';
import styles from './styles';
import './stylescss.css';

export type LatLng = { lat?: string; lng?: string };

const TIMEOUT_GEOLOCATION = 10000;

const defaultPosition: LatLng = {
  lat: '13.7563',
  lng: '100.5018',
}

export const getLatLng8DecimalPlaces = (num: number) => {
  const fixedNum = UtilHelper.toFixedNumberWithoutRound(num, 8);
  return numeral(fixedNum).format('0,0.00000000');
};

const getFloatPosition = (position: any) => {
  return { lat: parseFloat(position.lat), lng: parseFloat(position.lng) };
};

// Without a position the map still needs a numeric center, so it falls back to
// defaultPosition — but draws no Marker, leaving the first pin to the merchant.
const MapWithAMarker = withScriptjs(
  withGoogleMap((props: any) => (
    <GoogleMap
      defaultZoom={16}
      defaultCenter={getFloatPosition(props.position || defaultPosition)}
      onClick={props.onPick}
      options={{ streetViewControl: false }}
    >
      {props.position ? (
        <Marker
          position={getFloatPosition(props.position)}
          draggable={true}
          onDragEnd={props.onPick}
        />
      ) : null}
    </GoogleMap>
  ))
);


type ShopLocationProps = {
  value: LatLng;
  onChange: (position: LatLng) => void;
  onConfirm?: (position: LatLng) => void;
  autoLocate?: boolean;
  showAddressChangedWarning?: boolean;
};

export const ShopLocation: React.FC<ShopLocationProps> = ({
  value,
  onChange,
  onConfirm,
  autoLocate,
  showAddressChangedWarning,
}) => {
  const [visible, setVisible] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<LatLng>({ lat: undefined, lng: undefined });
  const [selectPosition, setSelectPosition] = useState<LatLng>({ lat: undefined, lng: undefined });
  const hasPosition = Boolean(value.lat && value.lng);
  const hasSelectPosition = Boolean(selectPosition.lat && selectPosition.lng);

  useEffect(() => {
    if (!autoLocate) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCurrentPosition({
          lat: getLatLng8DecimalPlaces(pos.coords.latitude),
          lng: getLatLng8DecimalPlaces(pos.coords.longitude),
        });
      },
      (err) => {
        console.warn('geolocation unavailable, merchant must pin manually', err);
      },
      { timeout: TIMEOUT_GEOLOCATION }
    );
  }, []);

  const onShowModal = () => {
    // Re-seed the draft on every open so a cancelled edit does not linger.
    setSelectPosition(hasPosition ? value : currentPosition);
    setVisible(true);
  };

  const handleOk = () => {
    setVisible(false);

    const moved =
      selectPosition.lat !== value.lat || selectPosition.lng !== value.lng;
    if (moved) {
      onChange(selectPosition);
      onConfirm?.(selectPosition);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const pickPosition = (e: any) => {
    setSelectPosition({
      lat: getLatLng8DecimalPlaces(e.latLng.lat()),
      lng: getLatLng8DecimalPlaces(e.latLng.lng()),
    });
  };

  return (
    <>
      <Form.Item
        colon={false}
        required={true}
        label={<FormattedMessage id={'main.branchform.pinmap.label'} />}
      >
        <RBHBox style={styles.mapPreviewBox}>
          {hasPosition ? (
            <img
              data-testid='map_preview'
              src={
                `https://maps.googleapis.com/maps/api/staticmap?key=${envConfig.googleApiKey}&center=${value.lat},${value.lng}&zoom=16&scale=2&size=500x150&markers=color:red%7C${value.lat},${value.lng}`}
              alt="Map Preview"
              style={styles.mapPreviewImage}
              onClick={onShowModal}
            />
          ) : (
            <RBHImage
              data-testid='map_preview_default'
              id='map_preview_default'
              name='map_preview_default'
              style={styles.mapPreviewImage}
              src={Images.pinMap}
              onClick={onShowModal}
            />
          )}
        </RBHBox>
        {showAddressChangedWarning && (
          <RBHBox style={styles.mapWarningBox}>
            <ExclamationCircleFilled translate={null} style={styles.mapWarningIcon} />
            <RBHText
              data-testid={'map_address_changed_warning'}
              id={'map_address_changed_warning'}
              style={styles.mapWarning}
              text={'main.branchform.pinmap.addresschanged'}
            />
          </RBHBox>
        )}
      </Form.Item>
      <Form.Item
        colon={false}
        label={<FormattedMessage id={'main.branchform.latlng.label'} />}
      >
        <RBHBox className='latlng-box'>
          <RBHTextInput
            value={value.lat}
            data-testid='shopLatitude'
            id='shopLatitude'
            disabled={true}
            placeholder={'main.branchform.lat'}
            style={styles.latInput}
          />
          <RBHTextInput
            value={value.lng}
            data-testid='shopLongitude'
            id='shopLongitude'
            disabled={true}
            placeholder={'main.branchform.lng'}
            style={styles.lngInput}
          />
        </RBHBox>
      </Form.Item>

      <Modal
        closable={false}
        // GoogleMap reads defaultCenter only at mount, so remount it to recenter on the pin.
        destroyOnClose={true}
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        cancelText={null}
        footer={null}
      >
        <RBHBox style={styleModal.map}>
          <Space size={'middle'} direction={'vertical'}>
            <RBHText
              data-testid={'map_pin_title'}
              id={'map_pin_title'}
              style={styles.modalTitle}
              text={'main.branchform.pinmap.title'}
            />
          <RBHBox style={styles.mapHintBox}>
            <ExclamationCircleFilled translate={null} style={styles.mapHintIcon} />
            <RBHText
              data-testid={'map_pin_hint'}
              id={'map_pin_hint'}
              style={styles.mapHint}
              text={'main.branchform.pinmap.hint'}
            />
          </RBHBox>
          </Space>
          
          <MapWithAMarker
            googleMapURL={
              'https://maps.googleapis.com/maps/api/js?key=' +
              envConfig.googleApiKey
            }
            loadingElement={<div style={styles.mapLoadingElement} />}
            containerElement={<div style={styles.mapContainerElement} />}
            mapElement={<div style={styles.mapLoadingElement} />}
            position={hasSelectPosition ? selectPosition : undefined}
            onPick={pickPosition}
          />
          <RBHBox style={styles.modalSpacer} />
          <Row key='modalMap' justify='end' align='middle'>
            <Space size={'middle'}>
              <RBHButton
                data-testid={'cancel_map'}
                id={'cancel_map'}
                style={styleButton.buttonModalCancel}
                key='back'
                onClick={handleCancel}
              >
                <FormattedMessage id={'main.button_cancel'} />
              </RBHButton>
              <RBHButton
                data-testid={'confirm_map'}
                id={'confirm_map'}
                style={styleButton.buttonSave}
                key='submit'
                type='primary'
                disabled={!hasSelectPosition}
                onClick={handleOk}
              >
                <FormattedMessage id={'main.branchform.choosemap'} />
              </RBHButton>
            </Space>
          </Row>
        </RBHBox>
      </Modal>
    </>
  );
};
